import { PDF_PREVIEW_MAX_ZOOM, PDF_PREVIEW_MIN_ZOOM } from './previewZoom'

export interface Size {
    width: number
    height: number
}

export interface Point {
    x: number
    y: number
}

export const NO_PAN: Point = { x: 0, y: 0 }

/** Bounds only, never rounded: rounding here would swallow a wheel step smaller than half a percent. */
export const clampZoom = (zoom: number): number => Math.min(PDF_PREVIEW_MAX_ZOOM, Math.max(PDF_PREVIEW_MIN_ZOOM, zoom))

const WHEEL_ZOOM_SENSITIVITY = 0.0015
// `WheelEvent.deltaMode`: 0 is pixels; 1 is lines (a Firefox mouse wheel reports about 3 per notch); 2 is pages.
const WHEEL_DELTA_PIXELS = [1, 40, 800]

/** The zoom after one wheel event. Exponential, so zooming in and back out by the same distance returns to the start. */
export const zoomAfterWheel = (zoom: number, wheel: { deltaY: number; deltaMode: number }): number =>
    clampZoom(zoom * Math.exp(-wheel.deltaY * (WHEEL_DELTA_PIXELS[wheel.deltaMode] ?? 1) * WHEEL_ZOOM_SENSITIVITY))

/** Rotation is kept in 0 / 90 / 180 / 270, clockwise. */
export const rotateBy = (rotation: number, delta: 90 | -90): number => (((rotation + delta) % 360) + 360) % 360

const isQuarterTurn = (rotation: number): boolean => rotation % 180 !== 0

/** The box a `size` occupies on screen once rotated: a quarter turn swaps its sides. */
export const rotatedBox = (size: Size, rotation: number): Size => (isQuarterTurn(rotation) ? { width: size.height, height: size.width } : size)

/**
 * The unrotated size to draw an image at 100% so that its ROTATED box fits the frame. A CSS rotate does not
 * change the layout box, so fitting the unrotated image would clip a landscape scan turned to portrait.
 * Never upscales past the image's own pixels; zooming in is what the toolbar is for.
 */
export const fitImageSize = (natural: Size, frame: Size, rotation: number): Size => {
    const box = rotatedBox(natural, rotation)
    if (box.width <= 0 || box.height <= 0 || frame.width <= 0 || frame.height <= 0) return { width: 0, height: 0 }
    const scale = Math.min(frame.width / box.width, frame.height / box.height, 1)
    return { width: natural.width * scale, height: natural.height * scale }
}

const clampOffset = (offset: number, overflow: number): number => (overflow <= 0 ? 0 : Math.min(overflow, Math.max(-overflow, offset)))

/**
 * Pan is the offset of the content's centre from the frame's centre. Content that fits stays centred; content
 * larger than the frame can move until its edge meets the frame's edge, so it can never be dragged out of view.
 */
export const clampPan = (pan: Point, content: Size, frame: Size): Point => ({
    x: clampOffset(pan.x, (content.width - frame.width) / 2),
    y: clampOffset(pan.y, (content.height - frame.height) / 2),
})

/** The pan that keeps the spot under `point` (measured from the frame's centre) in place across a zoom change. */
export const panForZoomAtPoint = (pan: Point, zoom: number, nextZoom: number, point: Point): Point => {
    const ratio = nextZoom / zoom
    return { x: point.x - (point.x - pan.x) * ratio, y: point.y - (point.y - pan.y) * ratio }
}
